import FirebaseService from './FirebaseService'
import {
  ref,
  uploadBytesResumable as put,
  getDownloadURL,
  deleteObject
} from 'firebase/storage'

export class StorageException extends Error {
  message!: string
  name = 'StorageException'

  toString () {
    return `${this.name}: "${this.message}"`
  }
}

export default class StorageService {

  async upload (file: File, filePath?: string) {
    const storage = await FirebaseService.storage()
    if (!filePath || filePath === undefined) throw new StorageException('No file provided')

    // reformat the file name remove \s characters and . then replace them with _
    const fileExtension = file.name.split('.').splice(-1)[0]
    let fileName  = file.name.split('.').slice(0, -1).join('.')
    fileName = fileName.replace(/\s/g, '_').replace(/\./g, '_') + new Date().getTime() + '.' + fileExtension

    const storageRef = ref(storage, filePath + fileName)
    const metadata = this.getMetadata(file)

    try {
      const uploadTask = put(storageRef, file, metadata)
      return uploadTask
    } catch (err) {
      console.log('Error calling storage upload function')
    }
  }

  async download (filePath: string) {
    const storage = await FirebaseService.storage()
    const storageRef = ref(storage, filePath)

    const fileURL = await getDownloadURL(storageRef).catch(err => {
      switch (err.code) {
        case 'storage/object-not-found':
          throw new StorageException('File doesn\'t exist')
        case 'storage/unauthorized':
          throw new StorageException('User doesn\'t have permission to access the object')
        case 'storage/canceled':
          throw new StorageException('User canceled the upload')
        case 'storage/unknown':
          throw new StorageException('Unknown error occured')
      }
    })
    return fileURL
  }

  private getMetadata (file: File) {
    // @todo: add caching logic? cacheControl
    return {
      contentType: file.type,
      customMetadata: { size: this.formatBytes(file.size) }
    }
  }

  private formatBytes (bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  async removeUploadedImages (filePath: string) {
    const storage = await FirebaseService.storage()
    const storageRef = ref(storage, filePath)

    return await deleteObject(storageRef)
  }

  async deleteAttachmentWithStyledImages (filePath: string) {
    const imageTypes = ['png','jpg','jpeg','webp','gif','bmp','svg','ico','pjpeg']
    const storage = await FirebaseService.storage()
    const storageRef = ref(storage, filePath)
    const fileRef = storageRef.fullPath

    const fileRefArray = fileRef.split('/')
    const fullFileName = fileRefArray.pop()
    const fileName = fullFileName.split('.')[0]
    const ext = fullFileName.split('.').pop()
    const fileDir = fileRefArray.join('/')

    // attachment is an image
    if (imageTypes.includes(ext)) {
      const styles = ['200x200', '1080x1080', '1280x720', '1920x1080']
      const types = ['webp', ext]

      styles.forEach(style => {
        types.forEach(type => {
          const styledImageRef = ref(storage, fileDir + '/styles/' + fileName + '_' + style + '.' + type)
          deleteObject(styledImageRef).catch(err => {
            console.log('Error deleting styled image', err)
          })
        })
      })
    }

    return await deleteObject(storageRef)
  }

  async copyFile (sourcePath: string, destinationPath: string) {
    const storage = await FirebaseService.storage()
    const sourceRef = ref(storage, sourcePath)
    const destinationRef = ref(storage, destinationPath)

    const fileBlob = await (await fetch(await getDownloadURL(sourceRef))).blob()
    await put(destinationRef, fileBlob)
    return await getDownloadURL(destinationRef)
  }
}
